import { useEffect, useRef, useState } from 'react';

export interface SidebarCatalogNode {
  key: string;
  label: string;
  group: 'core' | 'pro' | 'product';
  children?: SidebarCatalogNode[];
}

const GROUP_LABELS: Record<SidebarCatalogNode['group'], string> = {
  core: 'Menú principal',
  pro: 'Lyrax Pro',
  product: 'Productos',
};

function collectKeys(node: SidebarCatalogNode): string[] {
  return [node.key, ...(node.children ?? []).flatMap(collectKeys)];
}

function leafKeys(node: SidebarCatalogNode): string[] {
  if (!node.children?.length) return [node.key];
  return node.children.flatMap(leafKeys);
}

function coreKeys(nodes: SidebarCatalogNode[]): string[] {
  return nodes.filter((node) => node.group === 'core').flatMap(collectKeys);
}

function MenuNode({
  node,
  selected,
  onToggle,
  depth,
}: {
  node: SidebarCatalogNode;
  selected: string[];
  onToggle: (node: SidebarCatalogNode, checked: boolean) => void;
  depth: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const leaves = leafKeys(node);
  const selectedCount = leaves.filter((key) => selected.includes(key)).length;
  const checked = leaves.length > 0 && selectedCount === leaves.length;
  const indeterminate = selectedCount > 0 && selectedCount < leaves.length;

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <div>
      <label
        className="flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer hover:bg-lyrax-bg"
        style={{ paddingLeft: `${depth * 1.1 + 0.5}rem` }}
      >
        <input
          ref={inputRef}
          type="checkbox"
          checked={checked}
          onChange={(event) => onToggle(node, event.target.checked)}
          className="w-4 h-4 rounded border-lyrax-border text-lyrax-primary focus:ring-lyrax-primary"
        />
        <span className="text-sm text-lyrax-text">{node.label}</span>
      </label>
      {node.children?.map((child) => (
        <MenuNode key={child.key} node={child} selected={selected} onToggle={onToggle} depth={depth + 1} />
      ))}
    </div>
  );
}

interface SidebarMenuEditorProps {
  value: string[] | null;
  onChange: (keys: string[]) => void;
}

export function SidebarMenuEditor({ value, onChange }: SidebarMenuEditorProps) {
  const [catalog, setCatalog] = useState<SidebarCatalogNode[]>([]);
  const [error, setError] = useState('');
  const initialized = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/super-admin/sidebar-catalog', {
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'No se pudo cargar el menú');
        if (!cancelled) setCatalog(data.data ?? []);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'No se pudo cargar el menú');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!catalog.length || value !== null || initialized.current) return;
    initialized.current = true;
    onChange(coreKeys(catalog));
  }, [catalog, value, onChange]);

  const toggle = (node: SidebarCatalogNode, checked: boolean) => {
    const keys = collectKeys(node);
    const current = new Set(value ?? []);
    for (const key of keys) {
      if (checked) current.add(key);
      else current.delete(key);
    }
    onChange([...current]);
  };

  if (error) {
    return <p className="text-sm text-red-700 dark:text-red-300">{error}</p>;
  }

  if (!catalog.length) {
    return <p className="text-sm text-lyrax-text-secondary">Cargando secciones del menú...</p>;
  }

  const selected = value ?? [];

  return (
    <div className="space-y-3">
      {(['core', 'pro', 'product'] as const).map((group) => {
        const nodes = catalog.filter((node) => node.group === group);
        if (!nodes.length) return null;
        return (
          <div key={group} className="rounded-lyrax border border-lyrax-border">
            <p className="border-b border-lyrax-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-lyrax-text-secondary">
              {GROUP_LABELS[group]}
            </p>
            {nodes.map((node) => (
              <MenuNode key={node.key} node={node} selected={selected} onToggle={toggle} depth={0} />
            ))}
          </div>
        );
      })}
    </div>
  );
}
